import { Navigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from '@/hooks/useAuth';
import { LoadingSkeleton } from '@/components/common/LoadingSkeleton';
import type { UserRole } from '@/types';

interface RoleRouteProps {
  /**
   * Roles permitted to access this route.
   * Users whose role is NOT in this list are redirected to /dashboard.
   *
   * Examples:
   *   allowedRoles={['admin']}               — admin-only (Gate Rules, KB, Settings)
   *   allowedRoles={['admin', 'reviewer']}   — admin + reviewer (Submit Contract)
   *   allowedRoles={['admin', 'auditor']}    — admin + auditor (Audit Trail export)
   */
  allowedRoles: UserRole[];
  children: React.ReactNode;
}

/**
 * Extends ProtectedRoute: also enforces a role allowlist.
 *
 * Behaviour:
 * - While auth is resolving: renders a full-page skeleton.
 * - Unauthenticated: redirects to /auth.
 * - Authenticated but role not in allowedRoles: redirects to /dashboard
 *   with a permission-denied toast.
 * - Authenticated with an allowed role: renders children.
 *
 * Use this instead of AdminRoute for all role-restricted pages.
 * AdminRoute is kept as a thin wrapper over RoleRoute for backward compat.
 */
export function RoleRoute({ allowedRoles, children }: RoleRouteProps) {
  const { profile, role, isLoading } = useAuth();

  if (isLoading) {
    return <LoadingSkeleton fullPage />;
  }

  if (!profile) {
    return <Navigate to="/auth" replace />;
  }

  if (!role || !allowedRoles.includes(role)) {
    toast.error('You do not have permission to access that page.');
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
}
